import React, { useContext } from 'react';
import Scroll from 'react-scroll';
import cn from 'classnames';
import ConfigContext from '../../../contexts/ConfigContext';

// Components
import LogoList from '../content/LogoList';
import Meta from '../../shared/Meta';
import ServicesForm from '../ServicesForm';
import Triple from '../content/Triple';
import CtaImage from '../content/CtaImage';
import FeaturedTrucks from '../FeaturedTrucks';
import SiteRequirements from '../content/SiteRequirements';
import Testimonials from '../content/Testimonials';
import BreweriesFaq from '../faqs/BreweriesFaq';

import styles from '../../../../stylesheets/public/layouts/AboutPage.module.postcss';
import tripleStyles from '../content/Triple.module.postcss';

const featuredTrucks = { featured: true };

function Breweries() {
  const { vehicle_type, city_name } = useContext(ConfigContext);
  const vehicleType = vehicle_type.toLowerCase();

  return (
    <div>
      <Meta
        title={`Food ${vehicle_type}s for Taprooms & Breweries`}
        breadcrumbName='Breweries'
        type='website'
        description={`Bring rotating food ${vehicleType}s to your brewery or taproom in ${city_name}. Curated food ${vehicleType} schedules that pair great food with great beer.`}
      />

      <div className='Section'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/Breweries.webp' noLazy isResponsive />
          <div className={styles.Cta_copy}>
            <h1>Food {vehicle_type}s &amp; Beer</h1>
            <p>Schedule a daily or weekly rotation of food {vehicleType}s to visit your brewery or taproom and sell food directly to your hungry customers. All at no cost to your business!</p>
            <p>
              <Scroll.Link
                smooth
                to='form'
                className='Button Button--mobile-w100'
                offset={-50}
              >
                Start the conversation
              </Scroll.Link>
            </p>
          </div>
        </div>
      </div>

      <div className='Section'>
        <LogoList breweries title='Partner with locations like' />
      </div>

      <section className='Section'>
        <h2>How It Works</h2>
        <div className={tripleStyles.Triple_wrapper}>
          <Triple
            image='https://static.seattlefoodtruck.com/photos/services1.webp'
            isResponsive
            title='📅 Curated Schedule'
            text={`Our platform automatically schedules ${city_name}'s best food ${vehicleType}s to visit your brewery or taproom on a daily rotation. By learning your customer's favorite food ${vehicleType}s, your schedule changes as it goes, with cuisines from all over the world.`}
          />
          <Triple
            image='https://static.seattlefoodtruck.com/photos/services2.webp'
            isResponsive
            title='📩 Fully Automated'
            text={`Your customers can receive weekly menu emails of food ${vehicleType}s scheduled each week. A customized web page is also built for your location to view that week's food ${vehicleType} lunch lineup. Leave the food program to the experts!`}
          />
          <Triple
            image='https://static.seattlefoodtruck.com/photos/services3.webp'
            isResponsive
            title='✅ Hassle-Free Experience'
            text={`Food ${vehicleType}s arrive, serve and sell lunch or dinner to your customers and leave with no mess behind. Customers can order at the truck or skip the line and order online. All food is made to order from a gourmet kitchen on wheels.`}
          />
        </div>
      </section>

      <FeaturedTrucks
        title={`Meet ${city_name}'s Best Food ${vehicleType}s`}
        options={featuredTrucks}
      />

      <section className='Section--extraPadding'>
        <h2>Serve Food at Your Brewery Without a Kitchen</h2>
        <div className={tripleStyles.Triple_wrapper + ' ' + tripleStyles.Triple_wrapper__quad}>
          <Triple
            image='https://static.seattlefoodtruck.com/photos/services4.webp'
            isResponsive
            title='🏆 Attract More Customers'
            text='Breweries that have on-site food see up to a 30% increase in beverage sales. Help build a sense of joint activity and community while giving your customers new dining options with their delicious beverages!'
          />
          <Triple
            image='https://static.seattlefoodtruck.com/photos/services5.webp'
            isResponsive
            title='⏳ Remove the Burden on Your Staff'
            text={`Juggling food ${vehicleType} schedules can be a full-time job for your taproom manager, with schedule changes, vendor no-shows, and keeping constant variety for your guests. Turn your food program on auto-pilot, with a rotating selection of delicious cuisines from the best food ${vehicleType}s.`}
          />
          <Triple
            image='https://static.seattlefoodtruck.com/photos/services6.webp'
            isResponsive
            title='🤝 Focus on Your Brewery, Not a Full Kitchen'
            text={`Kitchen build-outs can cost hundreds of thousands of dollars. and require constant staff. Food ${vehicleType}s can provide on-site options while maintaining safe distancing protocols, helping customers stay longer (and drink more).`}
          />
          <Triple
            image='https://static.seattlefoodtruck.com/photos/services7.webp'
            isResponsive
            title='🌍 Support Local Businesses'
            text={`Our owner-operated food ${vehicleType}s favor quality, variety and sustainability. Experience cuisines from all over the world right at your brewery.`}
          />
        </div>
      </section>

      <SiteRequirements />

      <section className='Section--extraPadding'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/bread.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h3>🍽️ Customer Credit Packages, Incentives and Rewards</h3>
            <p>Offer free meals, credits and a rewards program to incentivize current and prospective customers and give back to loyal customers.</p>
            <div>
              <Scroll.Link
                smooth
                to='form'
                className='Button Button--mobile-w100'
                offset={-50}
              >
                Get Started!
              </Scroll.Link>
            </div>
          </div>
        </div>
      </section>

      <Testimonials title='What People Are Saying' />

      <section className='Section--extraPadding'>
        <BreweriesFaq />
      </section>

      <Scroll.Element name='form'>
        <div className='GrayBg'>
          <section className='Section--extraPadding'>
            <h2>Let Us Help With Your Dining Program Needs</h2>
            <ServicesForm formType='breweries' />
          </section>
        </div>
      </Scroll.Element>
    </div>
  );
}

export default Breweries;
